import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Settings, Save, RotateCcw, Lock, Unlock, Loader2 } from 'lucide-react';
import type { WheelPrizeConfig } from '@/types/gems';

interface WheelPrizeConfigEditorProps {
  prizes: WheelPrizeConfig[];
  onSave: (prizes: WheelPrizeConfig[]) => Promise<boolean>;
  saving: boolean;
}

const WheelPrizeConfigEditor: React.FC<WheelPrizeConfigEditorProps> = ({
  prizes: initialPrizes,
  onSave,
  saving,
}) => {
  const [localPrizes, setLocalPrizes] = useState<WheelPrizeConfig[]>(initialPrizes);
  const [lockedSlots, setLockedSlots] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Sincronizar quando prizes externos mudam
  useEffect(() => {
    setLocalPrizes(initialPrizes);
    setHasChanges(false);
  }, [initialPrizes]);

  const totalProbability = localPrizes.reduce((sum, p) => sum + p.probability, 0);
  const isValid = Math.abs(totalProbability - 1) < 0.001;

  const toggleLock = useCallback((prizeId: string) => {
    setLockedSlots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(prizeId)) {
        newSet.delete(prizeId);
      } else {
        newSet.add(prizeId);
      }
      return newSet;
    });
  }, []);

  const handleProbabilityChange = useCallback((prizeId: string, newValue: number) => {
    setLocalPrizes(prevPrizes => {
      const targetPrize = prevPrizes.find(p => p.id === prizeId);
      if (!targetPrize) return prevPrizes;

      const oldValue = targetPrize.probability;
      const delta = newValue - oldValue;

      if (Math.abs(delta) < 0.001) return prevPrizes;

      // Slots que podem ser ajustados (não travados e não é o atual)
      const adjustableSlots = prevPrizes.filter(
        p => p.id !== prizeId && !lockedSlots.has(p.id) && p.probability > 0
      );

      if (adjustableSlots.length === 0) {
        // Não há slots para redistribuir, apenas ajustar o atual
        return prevPrizes.map(p =>
          p.id === prizeId ? { ...p, probability: Math.max(0, Math.min(1, newValue)) } : p
        );
      }

      // Total dos slots ajustáveis
      const adjustableTotal = adjustableSlots.reduce((sum, p) => sum + p.probability, 0);

      return prevPrizes.map(p => {
        if (p.id === prizeId) {
          return { ...p, probability: newValue };
        }
        
        if (lockedSlots.has(p.id)) {
          return p;
        }

        // Redistribuir delta proporcionalmente
        const ratio = p.probability / adjustableTotal;
        const adjustment = delta * ratio;
        const newProb = Math.max(0, p.probability - adjustment);
        
        return { ...p, probability: newProb };
      });
    });
    
    setHasChanges(true);
  }, [lockedSlots]);

  const handleReset = useCallback(() => {
    setLocalPrizes(initialPrizes);
    setHasChanges(false);
  }, [initialPrizes]);

  const handleSave = useCallback(async () => {
    const success = await onSave(localPrizes);
    if (success) {
      setHasChanges(false);
    }
  }, [localPrizes, onSave]);

  const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`;

  return (
    <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-luxury-pinkBright" />
            Configuração de Probabilidades
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge 
              className={`${isValid 
                ? 'bg-green-900/40 border-green-500/40 text-green-200' 
                : 'bg-red-900/40 border-red-500/40 text-red-200'
              }`}
            >
              Total: {formatPercentage(totalProbability)} {isValid ? '✓' : '✗'}
            </Badge>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Ajuste os sliders para configurar a chance de cada prêmio. Use o cadeado para travar slots específicos.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {localPrizes.map((prize) => {
          const isLocked = lockedSlots.has(prize.id);
          
          return (
            <div 
              key={prize.id} 
              className={`p-3 rounded-lg border transition-colors ${
                isLocked 
                  ? 'bg-yellow-900/10 border-yellow-500/30' 
                  : 'bg-black/30 border-white/10'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => toggleLock(prize.id)}
                  className={`p-1.5 rounded transition-colors ${
                    isLocked 
                      ? 'bg-yellow-900/30 text-yellow-400' 
                      : 'bg-white/5 text-muted-foreground hover:text-white'
                  }`}
                  title={isLocked ? 'Destravar' : 'Travar'}
                >
                  {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                </button>
                
                <span className="text-xl">{prize.display_emoji}</span>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {prize.display_label}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {prize.prize_type === 'nothing' && 'Nenhum prêmio'}
                    {prize.prize_type === 'gems' && `${prize.prize_value} gema(s)`}
                    {prize.prize_type === 'boost' && `${prize.prize_value} boost(s)`}
                    {prize.prize_type === 'vip_day' && `${prize.prize_value} dia(s) VIP`}
                  </p>
                </div>
                
                <Badge 
                  variant="outline" 
                  className={`min-w-[60px] justify-center ${
                    isLocked ? 'border-yellow-500/50 text-yellow-200' : 'border-white/20 text-white'
                  }`}
                >
                  {formatPercentage(prize.probability)}
                </Badge>
              </div>

              <Slider
                value={[prize.probability * 100]}
                onValueChange={([value]) => handleProbabilityChange(prize.id, value / 100)}
                min={0}
                max={100}
                step={0.5}
                disabled={isLocked}
                className={isLocked ? 'opacity-50' : ''}
              />
            </div>
          );
        })}

        {/* Ações */}
        <div className="flex gap-3 pt-4 border-t border-white/10">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges || saving}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Resetar
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={!hasChanges || !isValid || saving}
            className="flex-1 bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Configuração
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default WheelPrizeConfigEditor;
